import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, AlertCircle, Archive, ArchiveRestore, Zap, Settings, FileText, Megaphone, Calendar, Lightbulb, GraduationCap, BarChart3, Sparkles, TrendingUp, ShieldAlert, RefreshCw } from 'lucide-react';
import {
  useNotificationQuery,
  useUpdateNotificationItemMutation,
} from '@/lib/query-options';
import type { NotificationItem } from '@/lib/types';
import { useToast } from '@/hooks/useToast';

import { GroupedNotificationList } from './notifications/GroupedNotificationList';
import { SidebarContainer } from '@/components/glitch-ui/sidebar-container';

import { useAtomValue } from 'jotai';
import { organizationDataAtom } from '@/store';
import { getSourceDisplayName } from '@/utils/notificationCategories';

interface NotificationsInboxProps {
  selectedItemId?: string;
  onSelectItem?: (itemId: string) => void;
  activeTab: 'open' | 'archived';
  searchQuery?: string;
  apiFilters: {
    sources?: string[];
    warning_levels?: string[];
    start_date?: string;
    end_date?: string;
  };
  activeFilterCount: number;
}

export function NotificationsInbox({ selectedItemId, onSelectItem, activeTab, searchQuery, apiFilters, activeFilterCount }: NotificationsInboxProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const organizationData = useAtomValue(organizationDataAtom);

  // Mutations
  const updateNotificationMutation = useUpdateNotificationItemMutation();

  // Fetch notifications with pagination
  const { data: notificationData, isLoading, error } = useNotificationQuery(
    {
      ...apiFilters,
      search_query: searchQuery,
      is_archived: activeTab === 'archived',
      organization_id: organizationData?.id,
    },
    currentPage,
    pageSize
  );

  // Handlers
  const handleSelectItem = async (itemId: string) => {
    if (itemId === selectedItemId) {
      return;
    }

    const item = notificationData?.items.find((n) => n.id === itemId);
    if (!item) return;

    try {
      // Mark notification as viewed when clicked
      if (!item.is_viewed) {
        await updateNotificationMutation.mutateAsync({
          id: itemId,
          updates: { is_viewed: true },
        });
      }
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    } finally {
      // Navigate to the notification URL
      navigate({ to: '/notifications/$notificationId', params: { notificationId: itemId } });
      onSelectItem?.(itemId);
    }
  };

  const handleArchive = async (notificationId: string) => {
    try {
      await updateNotificationMutation.mutateAsync({
        id: notificationId,
        updates: { is_archived: true },
      });
    } catch (error) {
      console.error('Failed to archive notification:', error);
      toast({
        title: 'Error',
        description: 'Failed to archive notification. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleUnarchive = async (notificationId: string) => {
    try {
      await updateNotificationMutation.mutateAsync({
        id: notificationId,
        updates: { is_archived: false },
      });
    } catch (error) {
      console.error('Failed to unarchive notification:', error);
      toast({
        title: 'Error',
        description: 'Failed to unarchive notification. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleArchiveItem = async (itemId: string, event: React.MouseEvent, isArchived: boolean) => {
    event.stopPropagation();

    if (isArchived) {
      await handleUnarchive(itemId);
    } else {
      await handleArchive(itemId);
    }
  };

  const getStatusIcon = (notification: NotificationItem) => {
    if (notification.is_archived) {
      return <Archive className="h-4 w-4 text-gray-500" />;
    }
    switch (notification.warning_level) {
      case 'SUCCESS':
        // Show blue archive icon for viewed SUCCESS notifications, green tick for unviewed
        return notification.is_viewed
          ? <Archive className="h-4 w-4 text-blue-600" />
          : <Check className="h-4 w-4 text-green-600" />;
      case 'WARNING':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'ERROR':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'INFO':
      default:
        return <Archive className="h-4 w-4 text-blue-600" />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTitleForDisplay = (title: string) => {
    // For reports with date ranges, format them nicely
    if (title.includes(' - ') && title.match(/\d{4}-\d{2}-\d{2}/)) {
      return title.replace(' - ', '\n');
    }
    return title;
  };

  const getNotificationStyling = (notification: NotificationItem) => {
    if (notification.is_archived) {
      return {
        cardClass: '',
        borderClass: '',
      };
    }

    switch (notification.warning_level) {
      case 'ERROR':
        return {
          cardClass: 'bg-red-50 hover:bg-red-100',
          borderClass: 'border-l-4 border-red-500',
        };
      case 'WARNING':
        return {
          cardClass: 'bg-yellow-50 hover:bg-yellow-100',
          borderClass: 'border-l-4 border-yellow-500',
        };
      case 'SUCCESS':
        // Viewed SUCCESS notifications get plain white styling like INFO
        return notification.is_viewed
          ? {
              cardClass: '',
              borderClass: 'border-l-4 border-transparent',
            }
          : {
              cardClass: 'bg-green-50 hover:bg-green-100',
              borderClass: 'border-l-4 border-green-500',
            };
      case 'INFO':
      default:
        // Unread INFO notifications use Cloudline background
        return notification.is_viewed
          ? {
              cardClass: '',
              borderClass: 'border-l-4 border-transparent',
            }
          : {
              cardClass: 'bg-gray-50 hover:bg-gray-100',
              borderClass: 'border-l-4 border-gray-200',
            };
    }
  };

  // Source configuration for icons and colors
  const SOURCE_CONFIG: Record<string, { label: string; icon: React.ElementType; color: string }> = {
    ACTIONS: {
      label: 'Actions',
      icon: Zap,
      color: 'text-blue-600',
    },
    CONFIGURATION: {
      label: 'Configuration',
      icon: Settings,
      color: 'text-purple-600',
    },
    WEEKLY_PERFORMANCE_REPORT: {
      label: 'Reports',
      icon: FileText,
      color: 'text-green-600',
    },
    CAMPAIGN_CREATION: {
      label: 'Campaign Creation',
      icon: Megaphone,
      color: 'text-amber-600',
    },
    daily_summary: {
      label: 'Daily Summary',
      icon: Calendar,
      color: 'text-indigo-600',
    },
    intelligence_report: {
      label: 'Intelligence Report',
      icon: Lightbulb,
      color: 'text-yellow-600',
    },
    onboarding: {
      label: 'Onboarding',
      icon: GraduationCap,
      color: 'text-teal-600',
    },
    strategic_report: {
      label: 'Google Ads Report',
      icon: BarChart3,
      color: 'text-rose-600',
    },
    v0_recommendations: {
      label: 'Recommendations (v0)',
      icon: Sparkles,
      color: 'text-violet-600',
    },
    v1_recommendations: {
      label: 'Recommendations (v1)',
      icon: TrendingUp,
      color: 'text-cyan-600',
    },
    keyword_sync: {
      label: 'Policy Violations',
      icon: ShieldAlert,
      color: 'text-red-600',
    },
    googleads_campaign_sync: {
      label: 'Google Ads Sync',
      icon: RefreshCw,
      color: 'text-orange-600',
    },
  };

  // Get source configuration
  const getSourceConfig = (source: string) => {
    return SOURCE_CONFIG[source] || {
      label: getSourceDisplayName(source),
      icon: FileText,
      color: 'text-gray-700',
    };
  };

  // Render notification card
  const renderNotificationCard = (notification: NotificationItem) => {
    const styling = getNotificationStyling(notification);
    const showArchiveButton = notification.warning_level === 'INFO' ||
      notification.is_archived ||
      (notification.warning_level === 'SUCCESS' && notification.is_viewed);

    const handleCardClick = () => {
      handleSelectItem(notification.id);
    };

    return (
      <Card
        className={`group m-1 rounded-md border-0 shadow-none transition-colors ${styling.cardClass} ${styling.borderClass} ${
          selectedItemId === notification.id
            ? styling.cardClass
              ? 'cursor-default'
              : 'bg-[#290BD9]/10 cursor-default'
            : styling.cardClass
            ? 'cursor-pointer'
            : 'hover:bg-gray-50 cursor-pointer'
        }`}
        onClick={handleCardClick}
      >
        <div className="relative p-2">
          <div className="pr-14">
            {/* Source Badge with Icon */}
            {notification.source && (() => {
              const config = getSourceConfig(notification.source);
              const SourceIcon = config.icon;
              return (
                <div className="mb-1 flex items-center gap-1.5">
                  <SourceIcon className={`h-4 w-4 ${config.color}`} />
                  <span className={`text-xs font-medium ${config.color}`}>
                    {config.label}
                  </span>
                </div>
              );
            })()}

            <div className="font-normal text-gray-900 leading-tight whitespace-pre-line">
              {formatTitleForDisplay(notification.title)}
            </div>
            <div className="text-sm text-muted-foreground">{formatDate(notification.created_at)}</div>
          </div>

          <div className="absolute top-1/2 right-5 transform -translate-y-1/2">
            {showArchiveButton ? (
              <Button
                disabled={updateNotificationMutation.isPending}
                onClick={(e) => handleArchiveItem(notification.id, e, notification.is_archived)}
                variant="ghost"
                size="sm"
                className="group/btn h-8 w-8 p-0 flex items-center justify-center hover:bg-gray-100"
              >
                {notification.is_archived ? (
                  <ArchiveRestore className="h-4 w-4 text-gray-400 transition-colors group-hover/btn:text-gray-900" />
                ) : (
                  <Archive className="h-4 w-4 text-gray-400 transition-colors group-hover/btn:text-gray-900" />
                )}
              </Button>
            ) : (
              <div className="h-8 w-8 flex items-center justify-center">
                {getStatusIcon(notification)}
              </div>
            )}
          </div>
        </div>
      </Card>
    );
  };

  // Only show full loading state on initial load (no data yet)
  if (isLoading && !notificationData) {
    return (
      <SidebarContainer>
        <div className="flex items-center justify-center p-6 text-gray-500">
          <span>Loading notifications...</span>
        </div>
      </SidebarContainer>
    );
  }

  if (error) {
    return (
      <SidebarContainer>
        <div className="flex items-center justify-center p-6 text-red-600">
          Error loading notifications
        </div>
      </SidebarContainer>
    );
  }

  const totalPages = notificationData ? Math.ceil(notificationData.total / notificationData.page_size) : undefined;

  return (
    <SidebarContainer
      pagination={notificationData && notificationData.total > pageSize && totalPages ? {
        currentPage,
        totalPages,
        onPreviousPage: () => setCurrentPage(p => Math.max(1, p - 1)),
        onNextPage: () => setCurrentPage(p => Math.min(totalPages, p + 1)),
        isPreviousDisabled: currentPage === 1,
        isNextDisabled: currentPage === totalPages,
      } : undefined}
    >
      <GroupedNotificationList
        notifications={notificationData?.items || []}
        hasActiveFilters={activeFilterCount > 0 || !!searchQuery}
        onSelectNotification={handleSelectItem}
        renderNotificationCard={renderNotificationCard}
      />
    </SidebarContainer>
  );
}